import React, { memo, useCallback } from 'react';
import { PLATFORM } from './Step2';

export enum OFFERINGS {
  CART_WIDGET = 'CART_WIDGET',
  CHECKOUT_PLUS = 'CHECKOUT_PLUS',
  CHECKOUT_WIDGET = 'CHECKOUT_WIDGET',
}

interface Step3Props {
  formData: {
    platform: PLATFORM;
    offering?: string;
    offeringNotes: string;
    useCustomApp: boolean;
  };
  handleOffering: (id: OFFERINGS) => void;
  handleChangeOfferingNotes: (
    e: React.ChangeEvent<HTMLTextAreaElement>,
  ) => void;
}

const offerings = [
  {
    id: OFFERINGS.CART_WIDGET,
    title: 'Cart Widget',
    description: 'Customizable cart experience with upsells',
    image: '/widgets/offering1.png',
    billingNote: 'Requires Custom App',
    disabled: true,
  },
  {
    id: OFFERINGS.CHECKOUT_PLUS,
    title: 'Checkout Plus',
    description: 'Advanced opt-in through Checkout button',
    image: '/widgets/offering2.png',
    billingNote: 'Supported in all store types',
    disabled: false,
  },
  {
    id: OFFERINGS.CHECKOUT_WIDGET,
    title: 'Checkout Extension',
    description: 'Lightweight opt-in widget for Shopify Plus checkout',
    image: '/widgets/offering3.png',
    billingNote: 'Requires Shopify Plus',
    disabled: true,
  },
];

const Step3: React.FC<Step3Props> = ({
  formData,
  handleOffering,
  handleChangeOfferingNotes,
}) => {
  const getAvailableOfferings = useCallback(() => {
    let list = [...offerings];
    for (let i = 0; i < list.length; i++) {
      if (i === 0 && formData.useCustomApp) {
        list[i].disabled = false;
      }
      if (i === 2 && formData.platform === PLATFORM.SHOPIFY_PLUS) {
        list[i].disabled = false;
      }
    }

    return list;
  }, [formData.platform, formData.useCustomApp]);

  const selectedOffering = offerings.find((o) => o.id === formData.offering);

  return (
    <div>
      <div className="flex justify-start gap-2 mb-5 mx-auto w-[85%]">
        <div
          className="mr-5 w-[200px]"
          dangerouslySetInnerHTML={{
            __html: `<div style="position: relative; box-sizing: content-box; width: 100%; aspect-ratio: 1.7777777777777777;"><iframe src="https://guides.shipaid.com/embed/cmcwg4kan1gdz9empvs5o1l1w?embed_v=2" loading="lazy" title="Choose Your Integration Type" allow="clipboard-write" frameborder="0" webkitallowfullscreen="true" mozallowfullscreen="true" allowfullscreen style="width: 200px; height: 150px"></iframe></div>`,
          }}
        />
        <div className="text-left">
          <h2 className="text-3xl font-bold text-gray-900 mb-6">
            Choose Your Integration Type
          </h2>
          <p className="text-gray-600 mb-8">
            Choose how you want to offer SHIPAID to your customers.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {getAvailableOfferings().map((offering) => (
          <div
            key={offering.id}
            onClick={() =>
              offering.disabled ? null : handleOffering(offering.id)
            }
            className={`relative cursor-pointer border-2 rounded-lg overflow-hidden transition-all duration-200 hover:shadow-lg ${
              formData.offering === offering.id
                ? 'border-[#2563eb] ring-2 ring-blue-100'
                : 'border-gray-200 hover:border-[#2563eb]'
            } ${offering.disabled ? 'opacity-[0.5]' : ''}`}
          >
            <div className="aspect-video bg-gray-100 overflow-hidden">
              <img
                src={offering.image}
                alt={offering.title}
                className="w-full h-full object-contain"
              />
            </div>
            <div className="p-4">
              <h3 className="font-semibold text-gray-900 mb-2">
                {offering.title}
              </h3>
              <p className="text-sm text-gray-600 mb-3">
                {offering.description}
              </p>
              <div className="text-xs text-[#2563eb] font-medium">
                {offering.billingNote}
              </div>
            </div>
          </div>
        ))}
      </div>

      {selectedOffering && (
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-[#2563eb] font-medium">
            ✓{' '}
            {`${selectedOffering.title} selected: ${selectedOffering.billingNote}`}
          </p>
        </div>
      )}

      <div className="my-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Notes
        </label>
        <textarea
          value={formData.offeringNotes}
          onChange={handleChangeOfferingNotes}
          cols={5}
          placeholder="Additional notes..."
          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#2563eb] focus:border-transparent"
        />
      </div>
    </div>
  );
};

export default memo(Step3);
